import React, { useState, useEffect } from 'react'
import { fetchLanguageList, updateDefaultLanguage } from '@/services/fd-api.service'
import { getUserObjData, setUserObjData, checkIsLoggedIn } from '@common/services/cookies.service'
import ToastMessage, { notificationType as NotificationType } from '@/Components/FdComponents/ToastMessages'
import i18nTranslations from '@/i18n/translations'

type Language = {
  languageId: number
  languageCode: string
  displayName: string
  nativeName: string
}

type Toast = {
  id: number
  title: string
  message: string
  type: string
}

export type Translations = { [key: string]: string }

type LanguageContextType = {
  changeLanguage: (language: string) => void
  language: string
  translations: Translations
  languageList: Language[]
  loadTranslations: (componentName: string | string[]) => void
}

type LanguageContextProviderProps = {
  children: React.ReactNode
}

const DEFAULT_LANG = 'en'
const COMMON = 'common'

// Create the context
const LanguageContext = React.createContext<LanguageContextType>({} as LanguageContextType)

const LanguageContextProvider: React.FC<LanguageContextProviderProps> = ({ children }) => {
  const [toast, setToast] = useState<Toast>({} as Toast)
  const [language, setLanguage] = useState('')
  const [languageList, setLanguageList] = useState<Language[]>([])
  const [translations, setTranslations] = useState<Translations>({} as Translations)

  const getLanguageList = async () => {
    if (languageList.length) {
      return languageList
    }
    const languages = await fetchLanguageList()
    setLanguageList(languages)

    /* Temporarily hide languages drop-down */
    setUserObjData(DEFAULT_LANG, 'x-lang')
    setLanguage(DEFAULT_LANG)
    /*------------End-----------*/

    await setDefaultLanguage()
    return languages
  }

  const setDefaultLanguage = async () => {
    let lang = getUserObjData('x-lang')
    const lng = lang ? languageList.find((item) => item.languageCode === lang) : null
    if (!lang) {
      setUserObjData(DEFAULT_LANG, 'x-lang')
      setLanguage(DEFAULT_LANG)
    } else if (languageList.length && !lng) {
      setUserObjData(DEFAULT_LANG, 'x-lang')
      setLanguage(DEFAULT_LANG)
      const tdata = i18nTranslations[DEFAULT_LANG][COMMON]
      setToast({
        id: Date.now(),
        title: `${tdata.FAILED_TITLE}`,
        message: `${tdata.LANG_HAS_DELETED}`,
        type: NotificationType.Error
      })
    } else {
      setUserObjData(lang, 'x-lang')
      setLanguage(lang)
    }
  }

  useEffect(() => {
    getLanguageList()
  }, [])

  // Helper function to load translations from JSON files
  const loadTranslations = async (components: string | string[]) => {
    if (!language) {
      return
    }
    if (typeof components === 'string') {
      components = [components]
    }
    if (!i18nTranslations[language]) {
      changeLanguage(translations.LANG_CODE)
      setToast({
        id: Date.now(),
        title: `${translations.FAILED_TITLE}`,
        message: `${translations.LANG_NOT_FOUND}`,
        type: NotificationType.Error
      })
      return
    }
    // Start with common translations
    let mergedTranslations = { ...i18nTranslations[language][COMMON] }
    // Merge translations for each specified component
    for (const component of components) {
      const data = i18nTranslations[language][component] || {}
      mergedTranslations = { ...mergedTranslations, ...data }
    }

    setTranslations(mergedTranslations)
  }

  const changeLanguage = (lang: string) => {
    const isLoggedIn = checkIsLoggedIn()
    const { languageId } = languageList.find((item) => item.languageCode === lang) || {}
    if (!languageId) {
      return
    }
    if (!isLoggedIn) {
      setLanguage(lang)
      setUserObjData(lang, 'x-lang')
      return
    }
    updateDefaultLanguage(languageId)
      .then(() => {
        setLanguage(lang)
        setUserObjData(lang, 'x-lang')
      })
      .catch((e: any) => {
        if (e.status === 403) {
          setToast({
            id: Date.now(),
            title: `${translations.FAILED_TITLE}`,
            message: `${translations.LANG_NOT_FOUND}`,
            type: NotificationType.Error
          })
          const list = languageList.filter((i) => i.languageCode !== lang)
          setLanguageList(list)
        }
      })
  }

  return (
    <LanguageContext.Provider
      value={{
        language,
        languageList,
        translations,
        changeLanguage,
        loadTranslations
      }}
    >
      {children}
      {toast.id ? (
        <ToastMessage key={toast.id} title={toast.title} message={toast.message} type={toast.type as NotificationType} toastOpen={true} />
      ) : (
        ''
      )}
    </LanguageContext.Provider>
  )
}

// Custom hook for using the LanguageContext
const useLanguageContext = (): LanguageContextType => React.useContext(LanguageContext)
export { useLanguageContext }

export default LanguageContextProvider
